import {
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { v2MenuItemDangerClass, V2ThemeProvider } from '@onevision-resources/design-theme';
import { formatSize } from '@provision/rich-text-editor';
import React, { useState } from 'react';
import { DynamicIcon } from 'src/components';
import { FileTypeBadge } from 'src/components/AddressFilesDropzone/file-type-badge';
import { getFileTypeFromContentType } from 'src/components/AddressFilesDropzone/utils';
import type { MoveAttachmentToSiteFilesAttachment } from 'src/graphql/entities/files-upload.types';
import { CONVERSATION_CARD_BORDER_RADIUS } from 'src/pages/Conversations/constants';
import { setConversationAttachmentDragData } from 'src/utils/conversation-attachment-drag';
import { COMPACT_ATTACHMENT_MIN_HEIGHT } from './attachment-message-content.utils';

type AttachmentThumbnailModel = {
  contentType: string;
  fileName: string;
  size: number;
};

export const FileAttachmentThumbnail = ({
  attachment,
  draggableAttachment,
  onAddToSiteFiles,
  onDelete,
  onRemove,
  compact = false,
}: {
  attachment: AttachmentThumbnailModel;
  draggableAttachment?: MoveAttachmentToSiteFilesAttachment;
  onAddToSiteFiles?: () => void;
  /** When provided, the menu offers a permanent delete (row + S3 file). */
  onDelete?: () => void;
  /** When provided, renders an inline "×" that removes this attachment (edit mode). */
  onRemove?: () => void;
  compact?: boolean;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const showRemove = Boolean(onRemove);
  // The "×" remove affordance takes precedence over the options menu in edit mode.
  const showAddToSiteFiles = Boolean(onAddToSiteFiles) && !showRemove;
  const showDelete = Boolean(onDelete) && !showRemove;
  const showMenu = showAddToSiteFiles || showDelete;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMoveToAddressFiles = () => {
    onAddToSiteFiles?.();
    handleMenuClose();
  };

  const handleDelete = () => {
    // Close first: the confirmation dialog owns the interaction from here.
    handleMenuClose();
    onDelete?.();
  };

  const handleDragStart = (event: React.DragEvent<HTMLElement>) => {
    if (!draggableAttachment) {
      return;
    }

    setConversationAttachmentDragData(event.dataTransfer, draggableAttachment);
  };

  return (
    <ListItem
      draggable={Boolean(draggableAttachment)}
      divider={!compact}
      onDragStart={handleDragStart}
      sx={{
        alignItems: 'center',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: CONVERSATION_CARD_BORDER_RADIUS,
        bgcolor: compact ? 'action.hover' : 'transparent',
        height: '100%',
        mb: compact ? 0 : 1,
        minHeight: compact ? (theme) => theme.spacing(COMPACT_ATTACHMENT_MIN_HEIGHT) : 'auto',
        overflow: 'hidden',
        px: compact ? 0.75 : 2,
        py: compact ? 0.375 : 0.25,
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        position: 'relative',
        '&:hover': {
          backgroundColor: compact ? 'action.selected' : 'action.hover',
          '& .menu-icon': {
            opacity: 1,
            pointerEvents: 'auto',
          },
        },
      }}
    >
      <ListItemAvatar
        sx={{
          display: 'flex',
          alignItems: 'center',
          minWidth: 'auto',
          mr: compact ? 0.75 : 2,
        }}
      >
        <FileTypeBadge
          type={getFileTypeFromContentType(attachment.contentType)}
          compact={compact}
        />
      </ListItemAvatar>

      <ListItemText
        sx={{ m: 0, overflow: 'hidden', maxWidth: compact ? '100%' : 250 }}
        primary={
          <Typography noWrap variant="body2" fontWeight="medium" title={attachment.fileName}>
            {attachment.fileName}
          </Typography>
        }
        secondary={
          compact ? null : (
            <Typography variant="caption" color="text.secondary" noWrap>
              {formatSize(attachment.size)}
            </Typography>
          )
        }
      />

      {showMenu ? (
        <>
          <IconButton
            aria-label="more options"
            onClick={handleMenuOpen}
            size="small"
            className="menu-icon"
            sx={{
              flexShrink: 0,
              ml: 'auto',
              p: compact ? 0.25 : undefined,
              opacity: { xs: 1, sm: open ? 1 : 0 },
              pointerEvents: { xs: 'auto', sm: open ? 'auto' : 'none' },
              transition: 'opacity 0.2s ease',
            }}
          >
            <DynamicIcon name="more-vertical" library="lucide" />
          </IconButton>
          <V2ThemeProvider>
            <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose} keepMounted>
              {showAddToSiteFiles ? (
                <MenuItem onClick={handleMoveToAddressFiles}>
                  <ListItemIcon>
                    <DynamicIcon name="bookmark" library="lucide" />
                  </ListItemIcon>
                  <ListItemText>Add to Site Files</ListItemText>
                </MenuItem>
              ) : null}
              {showDelete ? (
                <MenuItem
                  data-testid="message-attachment-delete"
                  onClick={handleDelete}
                  className={v2MenuItemDangerClass}
                >
                  <ListItemIcon>
                    <DynamicIcon name="trash-2" library="lucide" />
                  </ListItemIcon>
                  <ListItemText>Delete</ListItemText>
                </MenuItem>
              ) : null}
            </Menu>
          </V2ThemeProvider>
        </>
      ) : null}

      {showRemove ? (
        <IconButton
          aria-label={`remove ${attachment.fileName}`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onRemove?.();
          }}
          size="small"
          sx={{
            flexShrink: 0,
            ml: 0.5,
            p: 0.25,
            color: 'text.secondary',
            '&:hover': { color: 'text.primary' },
          }}
        >
          <DynamicIcon name="x" library="lucide" size={18} />
        </IconButton>
      ) : null}
    </ListItem>
  );
};
